import moment from 'moment';
import { titleCase } from 'change-case';

import { trimMoney } from 'utils/strings';
import { generatePaymentLogPDFReceipt } from 'utils/receipt_pdf_generator';

import PaymentLogs from 'app/collections/payment_logs';
import ApiMode from 'app/models/api_mode';
import DateRange from 'app/models/date_range';
import DropdownSelection from 'app/models/dropdown_selection';
import PaymentLogReport from 'app/models/payment_log_report';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import RequestReportView from 'app/views/request_report_view';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import DateAndTimeHeadingView from 'app/components/list/item/headings/date_and_time';

import tmpl from './template';
import amountHeaderTemplate from './amount_header';
import descriptionHeaderTemplate from './description_header';
import receiptButtonTemplate from './receipt_button';

export const SOURCE_TYPE_READABLE = {
  user: 'Purchase',
  refund: 'Refund Credit',
  insurance: 'Insurance Credit',
  credit_card: 'Credit Card Charge',
  bank_account: 'Bank Account Charge',
  subscription: 'Subscription Payment',
};
export const CREDIT_TYPES = ['refund', 'insurance', 'credit_card', 'bank_account'];

export const AmountHeaderView = Base.extend({
  compiledTemplate: amountHeaderTemplate,

  context() {
    const sourceType = this.model.get('source_type');
    return {
      amount: trimMoney(this.model.get('amount'), 'USD'),
      isCredit: CREDIT_TYPES.includes(sourceType)
    };
  },
});

export const DescriptionHeaderView = Base.extend({
  compiledTemplate: descriptionHeaderTemplate,

  context() {
    const sourceType = this.model.get('source_type');
    return {
      sourceType: SOURCE_TYPE_READABLE[sourceType] || sourceType,
      last4: this.model.get('last4'),
    };
  },
});

export const ReceiptButtonView = Base.extend({
  compiledTemplate: receiptButtonTemplate,

  initialize({ user }) {
    this.user = user;
  },

  events: { 'click .pdf-file-bg-image': 'generateReceipt' },

  context() {
    return {
      receiptButton: this.model.get('status') === 'complete' && this.model.get('target_type') === 'easypost_stripe',
    }
  },

  generateReceipt(event) {
    event.preventDefault();
    const createdDate = moment(this.model.get('created_at') || '').utc();
    const sourceType = this.model.get('source_type');
    if (!createdDate.isValid()) throw new Error('created_at property not present');
    const filename = `easypost_${createdDate.format('YYYYMMDDHHmmss')}_${this.model.id}`;
    generatePaymentLogPDFReceipt(filename, {
      account: this.user.get('email'),
      name: this.user.get('name'),
      date: createdDate.format('YYYY-MM-DD'),
      time: createdDate.format('HH:mm:ss'),
      timezone: 'UTC',
      amount: trimMoney(this.model.get('amount'), 'USD'),
      transactionId: this.model.id,
      paymentMethod: SOURCE_TYPE_READABLE[sourceType] || sourceType,
      last4: this.model.get('last4'),
      plan: this.model.get('plan'),
    });
  },
});

export const buildHeadings = (user) => {
  return [
    new Heading({
      subView: m => new DateAndTimeHeadingView({ model: m }),
      display: 'Date',
      smSize: 2,
      xsHidden: true,
      wrapperClass: 'text-nowrap'
    }),
    new Heading({
      subView: m => new AmountHeaderView({ model: m }),
      display: 'Amount',
      smSize: 2,
      xsSize: 4,
      wrapperClass: 'text-right',
    }),
    new Heading({
      subView: m => new DescriptionHeaderView({ model: m }),
      display: 'Description',
      smSize: 4,
      xsSize: 4,
    }),
    new Heading({
      text: m => {
        const status = titleCase(m.get('status'));
        return (status == 'Creditable') ? 'Pending - Credited' : status;
      },
      display: 'Status',
      smSize: 2,
      xsSize: 4,
    }),
    new Heading({
      subView: m => new ReceiptButtonView({ model: m, user }),
      display: 'Receipt',
      smSize: 2,
      xsHidden: true,
    }),
  ];
};

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'payment-logs',

  initialize({ user }) {
    this.user = user;
    this.dateRange = new DateRange({});
    this.apiMode = new ApiMode({ mode: 'production' });
    this.sourceType = new DropdownSelection({
      choices: [
        { option: '', readable: 'All Transactions' },
        { option: 'user', readable: 'Purchases' },
        { option: 'refund', readable: 'Refund Credits' },
        { option: 'insurance', readable: 'Insurance Credits' },
        { option: 'credit_card', readable: 'Credit Card Charges' },
        { option: 'bank_account', readable: 'Bank Account Charges' },
        { option: 'subscription', readable: 'Subscription Payment' },
      ],
      paramKey: 'source_type',
    });

    this.collection = new PaymentLogs([], {
      dateRange: this.dateRange, sourceType: this.sourceType
    });

    this.listenTo(this.collection, 'update', this.render);

    this.addSubview('paymentLogDaterangePickers', '#payment_log_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));
    this.addSubview('requestPaymentLogReport', '#request_payment_log_report', new RequestReportView({ collection: this.collection, ReportClass: PaymentLogReport, dateRange: this.dateRange, apiMode: this.apiMode }));
    this.addSubview('sourceTypeSelector', '#source_type_selector', new SelectorView({ model: this.sourceType }));
    this.addSubview('paymentLogList', '#payment_log_list', new ListView({
      headings: buildHeadings(this.user),
      collection: this.collection,
      noResultsMessage: 'No payment logs are available to view.',
    }));
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/payment_logs/index.js